// src/services/feedbackSupport.js
/**
 * Helper utilities for interacting with the staff-facing feedback support API.
 *
 * All helpers expect to receive one of the request executors returned by useApiService
 * (e.g. executeGet, executePost). This keeps the logic easy to mock in tests.
 */

const ROOT = 'admin/feedback/support';

const encodeId = (id) => encodeURIComponent(id);

/**
 * Fetches a paginated list of feedback threads.
 * @param {(args:{url:string, params?:object})=>Promise<any>} requestFn executeGet instance
 * @param {{ limit?:number, offset?:number, status?:string|string[], category?:string, query?:string, sort?:string, order?:'asc'|'desc' }} params
 */
export async function fetchFeedbackThreads(requestFn, params = {}) {
  const {
    limit,
    offset,
    status,
    email,
  } = params;

  const finalParams = {
    ...(typeof limit === 'number' ? { limit } : {}),
    ...(typeof offset === 'number' ? { offset } : {}),
    ...(status ? { status } : {}),
    ...(email ? { email } : {}),
  };

  return requestFn({
    url: ROOT,
    params: finalParams,
  });
}

/**
 * Fetches thread metadata together with the latest messages.
 * @param {(args:{url:string})=>Promise<any>} requestFn executeGet instance
 * @param {string|number} threadId
 */
export async function fetchFeedbackThread(requestFn, threadId) {
  return requestFn({
    url: `${ROOT}/${encodeId(threadId)}`,
  });
}

/**
 * Fetches additional messages for a specific thread.
 * @param {(args:{url:string, params?:object})=>Promise<any>} requestFn executeGet instance
 * @param {string|number} threadId
 * @param {{ limit?:number, offset?:number, cursor?:string }} params
 */
export async function fetchFeedbackThreadMessages(requestFn, threadId, params = {}) {
  return requestFn({
    url: `${ROOT}/${encodeId(threadId)}/messages`,
    params,
  });
}

/**
 * Posts a staff reply message.
 * @param {(args:{url:string, data:any, headers?:object})=>Promise<any>} postFn executePost instance
 * @param {string|number} threadId
 * @param {{ message:string, staff_email?:string|null }} payload
 */
export async function postFeedbackThreadMessage(postFn, threadId, payload) {
  const trimmed = (payload?.message ?? '').trim();
  if (!trimmed) {
    const err = new Error('Message body cannot be empty.');
    err.code = 'invalid_payload';
    throw err;
  }

  return postFn({
    url: `${ROOT}/${encodeId(threadId)}/messages`,
    data: { message: trimmed },
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Updates the status of a feedback thread.
 * @param {(args:{url:string, data:any, headers?:object})=>Promise<any>} postFn executePost instance
 * @param {string|number} threadId
 * @param {string} status
 */
export async function updateFeedbackThreadStatus(postFn, threadId, status) {
  const cleanStatus = String(status || '').trim();
  if (!cleanStatus) {
    const err = new Error('Status cannot be empty.');
    err.code = 'invalid_status';
    throw err;
  }

  return postFn({
    url: `${ROOT}/${encodeId(threadId)}/status`,
    data: { status: cleanStatus },
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Normalises a raw thread object into a shape that is easier to consume within the UI.
 * @param {any} rawThread
 */
export function mapThread(rawThread) {
  if (!rawThread || typeof rawThread !== 'object') return null;
  const findValue = (targetKey) => {
    if (rawThread[targetKey] !== undefined) return rawThread[targetKey];
    const normalizedTarget = targetKey.replace(/\s+/g, '').toLowerCase();
    const matchKey = Object.keys(rawThread).find((key) =>
      key && key.replace(/\s+/g, '').toLowerCase() === normalizedTarget
    );
    return matchKey ? rawThread[matchKey] : undefined;
  };

  const createdAt = findValue('created_at');
  const updatedAt = findValue('updated_at');
  return {
    id: rawThread.id,
    subject: rawThread.subject ?? '-',
    category: rawThread.category ?? '',
    status: rawThread.status ?? '',
    customerEmail: rawThread.email ?? rawThread.customer_email ?? '',
    customerId: rawThread.customer_id ?? null,
    createdAt,
    updatedAt,
    lastMessage: rawThread.last_message ?? '',
    details: rawThread.details ?? '',
  };
}

/**
 * Normalises a message response object.
 * @param {any} rawMessage
 */
export function mapMessage(rawMessage) {
  if (!rawMessage || typeof rawMessage !== 'object') return null;
  return {
    id: rawMessage.id ?? rawMessage.message_id ?? `${rawMessage.created_at}-${rawMessage.direction ?? ''}`,
    direction: rawMessage.direction ?? 'user',
    message: rawMessage.message ?? rawMessage.body ?? '',
    createdAt: rawMessage.created_at ?? rawMessage.timestamp ?? null,
    raw: rawMessage,
  };
}
